/**
 * API Utility
 *
 * Centralized API client using axios and configuration from config.js
 *
 * Features:
 * - Dynamic API URL support (auto-detects host from browser origin)
 * - Automatic token refresh on 401 errors
 * - Request/response interceptors for auth and i18n
 */

import axios from 'axios';
import Cookies from 'js-cookie';
import config from '../config';
import i18n from '../i18n';
import { getApiBaseUrl } from './dynamicApiUrl';

/**
 * Create axios instance with default configuration
 */
const axiosInstance = axios.create({
  baseURL: config.api.baseURL,
  timeout: config.api.timeout,
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: {
    indexes: null, // No brackets for array params: types=1&types=2 instead of types[]=1&types[]=2
  },
});

/**
 * Request interceptor
 * Add authentication token and culture parameter to all requests
 */
axiosInstance.interceptors.request.use(
  (config) => {
    // Get token from cookies
    const token = Cookies.get('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add culture parameter to all requests
    const currentLanguage = i18n.language || 'en';
    if (!config.params) {
      config.params = {};
    }
    config.params.culture = currentLanguage;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Track refresh token state to prevent multiple refresh calls
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Response interceptor
 * Handle common response scenarios including refresh token logic
 */
axiosInstance.interceptors.response.use(
  (response) => {
    // For blob responses, return the full response object (headers needed for filename)
    if (response.config.responseType === 'blob') {
      return response;
    }
    // Return only the data from the response
    return response.data;
  },
  async (error) => {
    const originalRequest = error.config;

    // Handle common error scenarios
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;

      if (status === 401 && !originalRequest._retry) {
        const isLoginRequest = originalRequest.url.includes(config.endpoints.auth.login);
        const isRefreshRequest = originalRequest.url.includes(config.endpoints.auth.refresh);

        if (isLoginRequest) {
          Cookies.remove('token', { path: '/' });
          Cookies.remove('refreshToken', { path: '/' });
          Cookies.remove('user', { path: '/' });
          Cookies.remove('permissions', { path: '/' });
          return Promise.reject(data);
        }

        if (isRefreshRequest) {
          Cookies.remove('token', { path: '/' });
          Cookies.remove('refreshToken', { path: '/' });
          Cookies.remove('user', { path: '/' });
          Cookies.remove('permissions', { path: '/' });
          window.location.href = '/login';
          return Promise.reject(data);
        }

        // Get refresh token from cookies
        const refreshToken = Cookies.get('refreshToken');

        if (!refreshToken) {
          // No refresh token available, redirect to login
          Cookies.remove('token', { path: '/' });
          Cookies.remove('user', { path: '/' });
          window.location.href = '/login';
          return Promise.reject(data);
        }

        if (isRefreshing) {
          // If already refreshing, queue this request
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers.Authorization = `Bearer ${token}`;
              return axiosInstance(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        try {
          // Call refresh token API (use dynamic URL to ensure correct host)
          const currentBaseUrl = getApiBaseUrl();
          const response = await axios.post(
            `${currentBaseUrl}${config.endpoints.auth.refresh}`,
            { refreshToken },
            {
              headers: {
                'Content-Type': 'application/json',
              },
            }
          );

          const { token: newToken, refreshToken: newRefreshToken } = response.data;

          // Update tokens in cookies
          Cookies.set('token', newToken, { path: '/' });
          if (newRefreshToken) {
            Cookies.set('refreshToken', newRefreshToken, { path: '/' });
          }

          // Update authorization header
          axiosInstance.defaults.headers.Authorization = `Bearer ${newToken}`;
          originalRequest.headers.Authorization = `Bearer ${newToken}`;

          // Process queued requests
          processQueue(null, newToken);

          // Retry original request
          return axiosInstance(originalRequest);
        } catch (refreshError) {
          // Refresh failed, clear cookies and redirect to login
          processQueue(refreshError, null);
          Cookies.remove('token', { path: '/' });
          Cookies.remove('refreshToken', { path: '/' });
          Cookies.remove('user', { path: '/' });
          window.location.href = '/login';
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      switch (status) {
        case 403:
          // Forbidden
          console.error('Access forbidden:', data.message);
          break;
        case 404:
          // Not found
          console.error('Resource not found:', data.message);
          break;
        case 500:
          // Server error
          console.error('Server error:', data.message);
          break;
        default:
          if (status !== 401) {
            console.error('API error:', data.message);
          }
      }

      return Promise.reject(data);
    } else if (error.request) {
      // Request made but no response received
      console.error('No response from server');
      return Promise.reject({ message: 'No response from server' });
    } else {
      // Error in request setup
      console.error('Request error:', error.message);
      return Promise.reject({ message: error.message });
    }
  }
);

/**
 * API Client wrapper
 */
const apiClient = {
  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @param {Object} config - Additional axios config
   * @returns {Promise} Response data
   */
  get: (endpoint, params = {}, config = {}) => {
    return axiosInstance.get(endpoint, { params, ...config });
  },

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @param {Object} config - Additional axios config
   * @returns {Promise} Response data
   */
  post: (endpoint, data = {}, config = {}) => {
    return axiosInstance.post(endpoint, data, config);
  },

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @param {Object} config - Additional axios config
   * @returns {Promise} Response data
   */
  put: (endpoint, data = {}, config = {}) => {
    return axiosInstance.put(endpoint, data, config);
  },

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} config - Additional axios config
   * @returns {Promise} Response data
   */
  delete: (endpoint, config = {}) => {
    return axiosInstance.delete(endpoint, config);
  },

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body
   * @param {Object} config - Additional axios config
   * @returns {Promise} Response data
   */
  patch: (endpoint, data = {}, config = {}) => {
    return axiosInstance.patch(endpoint, data, config);
  },

  /**
   * Download file request
   * @param {string} endpoint - API endpoint
   * @param {string} filename - Optional filename for download
   * @param {Object} params - Query parameters
   * @returns {Promise} Triggers file download
   */
  download: async (endpoint, filename = null, params = {}) => {
    const response = await axiosInstance.get(endpoint, {
      params,
      responseType: 'blob',
    });

    // Get filename from Content-Disposition header or use provided filename
    const contentDisposition = response.headers?.['content-disposition'];
    let downloadFilename = filename;
    if (!downloadFilename && contentDisposition) {
      const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
      if (filenameMatch && filenameMatch[1]) {
        downloadFilename = filenameMatch[1].replace(/['"]/g, '');
      }
    }
    if (!downloadFilename) {
      downloadFilename = 'download';
    }

    // Create blob URL and trigger download
    const responseData = response.data || response;
    const blob = responseData instanceof Blob ? responseData : new Blob([responseData]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', downloadFilename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    return { success: true, filename: downloadFilename };
  },
};

// Export axios instance for advanced usage
export { axiosInstance };

// Export API client as default
export default apiClient;

/**
 * Example usage in components:
 *
 * import api from '@utils/api';
 * import config from '@/config';
 *
 * // Login
 * const loginUser = async (username, password) => {
 *   const data = await api.post(config.endpoints.auth.login, {
 *     username,
 *     password
 *   });
 *   return data;
 * };
 *
 * // Get device list
 * const getDeviceList = async () => {
 *   const data = await api.get(config.endpoints.devices.list);
 *   return data;
 * };
 *
 * // Create new user
 * const createUser = async (userData) => {
 *   const data = await api.post(config.endpoints.users.create, userData);
 *   return data;
 * };
 */
